import React, { useState } from 'react';
import { Search, Loader, ExternalLink, Building2, Users, Globe, Mail } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import AppLayout from '../../components/AppLayout';

export default function StudioDiscovery() {
  const [query, setQuery] = useState('');
  const [targetCount, setTargetCount] = useState(50);
  const [region, setRegion] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [parentCompanies, setParentCompanies] = useState([]);

  const handleDiscover = async () => {
    if (!query) {
      toast.error('Please enter a search query');
      return;
    }
    
    setSearching(true);
    setResults([]); // Clear previous results
    
    try {
      console.log('Discovering studios with:', { query, target_count: targetCount, region });
      const r = await api.post('/api/studio-discovery/discover', {
        query,
        target_count: targetCount,
        region: region || undefined,
      });
      console.log('Discovery response:', r.data);
      
      const studios = r.data.studios || [];
      setResults(studios);
      
      if (studios.length === 0) {
        toast.info('No studios found. Try a different search query or region.');
      } else {
        toast.success(`Discovered ${studios.length} studio${studios.length === 1 ? '' : 's'}`);
      }
    } catch (err) {
      console.error('Discovery error:', err);
      const errorMsg = err?.response?.data?.detail || err?.message || 'Discovery failed';
      toast.error(`Discovery failed: ${errorMsg}`);
    } finally {
      setSearching(false);
    }
  };

  const loadParentCompanies = async () => {
    try {
      const r = await api.get('/api/studio-discovery/parent-companies');
      setParentCompanies(r.data.companies || []);
    } catch (err) {
      console.error('Load parent companies error:', err);
    }
  };

  const handleConfirm = async (studio) => {
    try {
      await api.post('/api/studio-discovery/review/confirm', {
        studio_id: studio.id,
        notes: '',
      });
      toast.success('Studio added to CRM!');
      setResults(prev => prev.filter(s => s.id !== studio.id));
    } catch (err) {
      console.error('Confirm error:', err);
      toast.error('Failed to add studio');
    }
  };

  const handleSkip = (studioId) => {
    setResults(prev => prev.filter(s => s.id !== studioId));
  };

  return (
    <AppLayout title="Studio & Crew Discovery" overline="AUTOMATED DISCOVERY">
      <div className="max-w-7xl mx-auto">
        {/* Search Form */}
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Search Query</label>
              <input
                type="text"
                placeholder="e.g. gay porn studios Europe"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Region (optional)</label>
              <input
                type="text"
                placeholder="e.g. Europe, USA"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
          </div>
          <div className="mt-4 flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Target Count</label>
              <input
                type="number"
                min="10"
                max="100"
                value={targetCount}
                onChange={(e) => setTargetCount(parseInt(e.target.value) || 50)}
                className="w-32 bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <button
              onClick={handleDiscover}
              disabled={searching}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 text-white font-bold px-8 py-2.5 rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-purple-500/30">
              {searching ? <Loader className="animate-spin" size={18} /> : <Search size={18} />}
              {searching ? 'Discovering...' : 'Discover Studios'}
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <button
            onClick={loadParentCompanies}
            className="bg-zinc-800 hover:bg-zinc-700 border border-white/10 text-white px-4 py-2 rounded-lg transition flex items-center gap-2">
            <Building2 size={18} />
            View Parent Companies
          </button>
        </div>

        {/* Parent Companies */}
        {parentCompanies.length > 0 && (
          <div className="bg-zinc-900 border border-white/10 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Parent Companies</h2>
            <div className="space-y-3">
              {parentCompanies.map((company, i) => (
                <div key={i} className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="text-white font-semibold mb-2">{company.name}</div>
                  {company.sub_brands?.length > 0 && (
                    <div className="text-sm text-zinc-400">
                      Sub-brands: {company.sub_brands.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Discovered Studios ({results.length})</h2>
            {results.map(studio => (
              <div key={studio.id} className="bg-zinc-900 border border-white/10 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{studio.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-zinc-400">
                      {studio.city && studio.country && (
                        <span>📍 {studio.city}, {studio.country}</span>
                      )}
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-semibold">{studio.type}</span>
                      <span className="px-2 py-1 bg-zinc-700 text-zinc-300 rounded text-xs">{studio.status}</span>
                    </div>
                  </div>
                </div>

                {studio.legal_entity_name && (
                  <p className="text-zinc-400 text-sm mb-2">Legal: {studio.legal_entity_name}</p>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                  {studio.website && (
                    <a href={studio.website} target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
                      <Globe size={14} /> Website
                    </a>
                  )}
                  {studio.email && (
                    <span className="text-zinc-400 text-sm flex items-center gap-1">
                      <Mail size={14} /> {studio.email}
                    </span>
                  )}
                  {studio.twitter_x && (
                    <a href={`https://x.com/${studio.twitter_x}`} target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
                      𝕏 @{studio.twitter_x}
                    </a>
                  )}
                  {studio.instagram && (
                    <a href={`https://instagram.com/${studio.instagram}`} target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
                      📸 @{studio.instagram}
                    </a>
                  )}
                </div>

                {studio.parent_company_name && (
                  <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg">
                    <span className="text-xs font-semibold text-zinc-400 uppercase">Parent Company</span>
                    <p className="text-white font-semibold mt-1">{studio.parent_company_name}</p>
                  </div>
                )}

                {studio.ai_outreach_priority && (
                  <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <span className="text-xs font-semibold text-purple-300 uppercase">AI Manager Priority</span>
                    <p className="text-white mt-1">{studio.ai_outreach_priority}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <button
                    onClick={() => handleConfirm(studio)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg transition">
                    Confirm & Add to CRM
                  </button>
                  <button
                    onClick={() => handleSkip(studio.id)}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-semibold py-2 rounded-lg transition">
                    Skip
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!searching && results.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p>No results yet. Enter a search query and click "Discover Studios"</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
